import { Box, Typography, Button, Card, CardContent, Grid, Chip, Divider, Stack, Checkbox } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useTireBatchList } from '../../../../stores/tireBatchStore';
import { useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useLanguage } from '../../../../utils/i18n';

// Helper to extract tire detail fields from batch item (handles nested join structure)
const extractTireDetail = (t) => ({
  brand: t?.join?.tire_item_tire_brand_name || t?.tire_brand_name || t?.join?.tire_brand_name || '',
  size: t?.join?.tire_item_tire_size_code || t?.tire_size_code || t?.join?.tire_size_code || '',
  pattern: t?.join?.m_tire_spec_pattern || t?.m_tire_spec_pattern || t?.tire_spec_name || t?.join?.tire_spec_name || '',
  serial: t?.serial_number || t?.batch_number || '',
  status_backlog: t?.status_backlog || ''
});

const getBacklogColor = (status) => {
  if (!status) return 'default';
  const s = status.toUpperCase();
  if (s === 'CRITICAL') return 'error';
  if (s === 'HIGH') return 'warning';
  if (s === 'MEDIUM') return 'info';
  return 'default';
};

export default function TireActionList({
  vehicleDetails,
  companyId,
  positionMap,
  installedTires,
  onInstallClick,
  onRemoveClick,
  mode = 'action',
  onInspectClick,
  onInspectBulkClick,
  selectedTire,
  onSelectTire,
  onTiresDataChange
}) {
  const { t } = useLanguage();
  const [selectedTires, setSelectedTires] = useState([]);
  const { data: allTires, isLoading: isLoadingTires } = useTireBatchList(0, 10000, companyId ? [{ value: companyId }] : []);

  const handleToggleSelect = (tire) => {
    if (mode === 'inspect' && tire.lastInspection?.tire_damage && tire.status_backlog) {
      return;
    }
    setSelectedTires((prev) => {
      const exists = prev.some((t) => t.id === tire.id);
      if (exists) {
        return prev.filter((t) => t.id !== tire.id);
      } else {
        return [...prev, tire];
      }
    });
  };

  const handleRemoveSelected = () => {
    if (selectedTires.length > 0) {
      onRemoveClick(selectedTires);
      setSelectedTires([]);
    }
  };

  const vehicleTires = useMemo(() => {
    if (!vehicleDetails || !installedTires) return [];

    const tireDetailMap = {};
    if (allTires && allTires.length > 0) {
      allTires.forEach((t) => {
        const sn = t.serial_number || t.batch_number || '';
        if (sn) tireDetailMap[sn] = t;
      });
    }

    // Iterate over installed tires from Axle Map (this is the source of truth)
    const result = [];
    for (const posCode in installedTires) {
      const installed = installedTires[posCode];
      if (!installed || !installed.serial_number) continue;

      const raw = tireDetailMap[installed.serial_number];
      const detail = raw ? extractTireDetail(raw) : {};

      const getValidValue = (v1, v2, v3) => {
        if (v1 != null && v1 !== 0 && String(v1) !== '0') return v1;
        if (v2 != null && v2 !== 0 && String(v2) !== '0') return v2;
        if (v3 != null && v3 !== 0 && String(v3) !== '0') return v3;
        return null;
      };

      const lastInspection = raw?.last_inspection;
      const air_pressure = getValidValue(lastInspection?.air_pressure, raw?.air_pressure, raw?.join?.m_tire_spec_pressure_std);
      const tread_depth = getValidValue(lastInspection?.tread_depth, raw?.tread_depth, raw?.join?.m_tire_spec_original_tread_depth);

      const otd = raw?.join?.m_tire_spec_original_tread_depth;
      const tdForWear = getValidValue(lastInspection?.tread_depth, raw?.tread_depth, null);

      let wearPercentage = null;
      if (otd && otd > 0 && tdForWear != null) {
        const wear = ((otd - tdForWear) / otd) * 100;
        wearPercentage = Math.round(wear);
      }

      let inspectionData = null;
      if (lastInspection || air_pressure !== null || tread_depth !== null) {
        inspectionData = {
          date: lastInspection?.inspection_date
            ? dayjs(lastInspection.inspection_date).format('DD-MM-YYYY')
            : lastInspection?.created_date
              ? dayjs(lastInspection.created_date).format('DD-MM-YYYY')
              : '-',
          tread_depth,
          air_pressure,
          condition: lastInspection?.condition || '',
          status_backlog: lastInspection?.status_backlog,
          wear_percentage: wearPercentage,
          tire_damage: lastInspection?.tire_damage ?? null,
          tire_damage_name: raw?.is_need_action === false ? '' : lastInspection?.tire_damage_name || ''
        };
      }

      result.push({
        id: raw?.id || installed.tire_item || installed.tire_item_id || posCode,
        tire_item: raw?.tire_item || installed.tire_item || installed.tire_item_id || raw?.tire_item_id,
        tire_item_id: raw?.tire_item || installed.tire_item || installed.tire_item_id || raw?.tire_item_id,
        serial_number: installed.serial_number,
        tire_brand_name: detail.brand || '',
        tire_size_code: detail.size || '',
        tire_spec_name: detail.pattern || '',
        status_backlog: raw?.is_need_action === false ? '' : detail.status_backlog || raw?.status_backlog || '',
        posCode,
        posLabel: positionMap[posCode]?.label || posCode,
        lastInspection: inspectionData,
        last_inspection: lastInspection || null,
        base_condition: raw?.base_condition || '',
        air_pressure: raw?.air_pressure || 0,
        tread_depth: raw?.tread_depth || 0,
        otd: raw?.join?.m_tire_spec_original_tread_depth || 0
      });
    }

    return result;
  }, [allTires, vehicleDetails, installedTires, positionMap]);

  useEffect(() => {
    if (onTiresDataChange) {
      onTiresDataChange(vehicleTires);
    }
  }, [vehicleTires, onTiresDataChange]);

  useEffect(() => {
    if (selectedTire) {
      const el = document.getElementById(`tire-card-${selectedTire}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedTire]);

  if (!vehicleDetails) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('tireCycle.selectVehicleToManageTires', 'Please select a vehicle to manage tires.')}
        </Typography>
      </Box>
    );
  }

  const isLoading = isLoadingTires;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h6">{t('tireCycle.installedTires', 'Installed Tires')}</Typography>
        <Stack direction="row" spacing={2}>
          {mode === 'action' && selectedTires.length > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleRemoveSelected}>
              {`${t('tireCycle.removeSelected', 'Remove Selected')} (${selectedTires.length})`}
            </Button>
          )}
          {mode === 'action' && onInstallClick && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onInstallClick}>
              {t('tireCycle.installTire', 'Install Tire')}
            </Button>
          )}
          {mode === 'inspect' && selectedTires.length > 0 && onInspectBulkClick && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={() => {
                onInspectBulkClick(selectedTires);
                setSelectedTires([]);
              }}
            >
              {`${t('tireCycle.inspectSelectedTires', 'Inspect Selected Tires')} (${selectedTires.length})`}
            </Button>
          )}
        </Stack>
      </Box>

      {isLoading ? (
        <Typography>{t('tireCycle.loadingInstalledTires', 'Loading installed tires...')}</Typography>
      ) : vehicleTires.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
          <Typography variant="body1" color="text.secondary">
            {t('tireCycle.noTiresCurrentlyInstalled', 'No tires currently installed on this vehicle.')}
          </Typography>
          {mode === 'action' && onInstallClick && (
            <Button variant="outlined" sx={{ mt: 2 }} onClick={onInstallClick}>
              {t('tireCycle.installFirstTire', 'Install First Tire')}
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, minHeight: 0 }}>
          <Grid container spacing={1}>
            {vehicleTires.map((tire) => {
              const isInspectDisabled = mode === 'inspect' && !!(tire.lastInspection?.tire_damage && tire.status_backlog);

              return (
                <Grid item size={{ xs: 12 }} key={tire.id}>
                  <Card
                    id={`tire-card-${tire.posCode}`}
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTire && onSelectTire(tire.posCode === selectedTire ? null : tire.posCode);
                    }}
                    sx={{
                      cursor: 'pointer',
                      borderColor: selectedTire === tire.posCode ? 'primary.main' : 'divider',
                      boxShadow: selectedTire === tire.posCode ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                      '&:hover': { borderColor: 'primary.main', boxShadow: 1 }
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          flexWrap: { xs: 'wrap', md: 'nowrap' },
                          justifyContent: 'space-between',
                          gap: 2
                        }}
                      >
                        {(mode === 'action' || mode === 'inspect') && (
                          <Checkbox
                            checked={selectedTires.some((t) => t.id === tire.id)}
                            disabled={isInspectDisabled}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(tire);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0, mt: { xs: 0.5, sm: 0 } }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Chip label={tire.posLabel} size="small" color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {tire.serial_number}
                            </Typography>
                            {tire.lastInspection.status_backlog && (
                              <Chip
                                label={tire.lastInspection.status_backlog}
                                size="small"
                                color={getBacklogColor(tire.lastInspection.status_backlog)}
                              />
                            )}
                            {tire.lastInspection?.tire_damage_name && (
                              <Chip
                                label={`${t('tireCycle.damagePrefix', 'Damage: ')}${tire.lastInspection.tire_damage_name}`}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tire.tire_brand_name || '-'} • {tire.tire_size_code || '-'} • {tire.tire_spec_name || '-'}
                          </Typography>

                          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

                          {tire.lastInspection ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t('tireCycle.lastInspection', 'Last Inspection')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {tire.lastInspection.date}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t('tyreInstallation.treadDepth', 'Tread Depth')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {tire.lastInspection.tread_depth !== null ? `${tire.lastInspection.tread_depth} mm` : '-'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t('tireCycle.airPressureTh', 'Pressure')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {tire.lastInspection.air_pressure !== null ? `${tire.lastInspection.air_pressure} psi` : '-'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t('tireCycle.wear', 'Wear')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {tire.lastInspection.wear_percentage !== null ? `${tire.lastInspection.wear_percentage}%` : '-'}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              {t('tireCycle.noInspectionDataAvailable', 'No inspection data available')}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', ml: { xs: 'auto', md: 0 } }}>
                          {mode === 'action' ? (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveClick([tire]);
                              }}
                            >
                              {t('buttonTitle.remove', 'Remove')}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              startIcon={<SearchIcon />}
                              disabled={isInspectDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                onInspectClick(tire);
                              }}
                            >
                              {t('buttonTitle.inspect', 'Inspect')}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
